import { useState, useEffect } from 'react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Activity, Sparkles, Building2, Coins } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard')
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#A3A3A3] font-ibm" data-testid="dashboard-loading">Kontrol paneli yükleniyor...</div>;
  if (!data) return <div className="text-[#737373] font-ibm">Kontrol paneli yüklenemedi</div>;

  const credits = data.credits || {};
  const availableCredits = (credits.current_balance || 0) - (credits.reserved_balance || 0);

  const stats = [
    { label: 'İçerik Varlıkları', value: data.content_count, icon: FileText, color: '#FACC15' },
    { label: 'Çalışan İşler', value: data.jobs_count, icon: Activity, color: '#3B82F6' },
    { label: 'Şablonlar', value: data.prompts_count, icon: Sparkles, color: '#10B981' },
    { label: user?.role === 'SUPER_ADMIN' ? 'Bayiler' : 'Bayi', value: data.tenants_count, icon: Building2, color: '#F59E0B' },
  ];

  const statusLabel = { pending: 'Bekliyor', processing: 'İşleniyor', done: 'Tamamlandı', failed: 'Başarısız' };
  const statusColor = {
    pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    processing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    done: 'bg-green-500/10 text-green-400 border-green-500/20',
    failed: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  return (
    <div data-testid="dashboard-page" className="animate-fade-in space-y-8">
      <div>
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Kontrol Paneli</h1>
        <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Tekrar hoş geldiniz, {user?.name}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="stat-card lg:col-span-1" data-testid="stat-credit-balance">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#737373] text-sm font-ibm">Kredi Bakiyesi</p>
                <p className="text-3xl font-outfit font-bold text-[#FACC15] mt-1">{availableCredits}</p>
                {credits.reserved_balance > 0 && (
                  <p className="text-xs text-blue-400 font-ibm mt-0.5">{credits.reserved_balance} rezerve</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-[#FACC15]/10">
                <Coins size={20} className="text-[#FACC15]" />
              </div>
            </div>
          </CardContent>
        </Card>
        {stats.map((s) => (
          <Card key={s.label} className="stat-card" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#737373] text-sm font-ibm">{s.label}</p>
                  <p className="text-3xl font-outfit font-bold text-white mt-1">{s.value}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: `${s.color}15` }}>
                  <s.icon size={20} style={{ color: s.color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-lg text-white">Son İşler</CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.recent_jobs || data.recent_jobs.length === 0) && (
              <p className="text-[#737373] text-sm font-ibm py-4">Henüz iş yok</p>
            )}
            <div className="space-y-3">
              {data.recent_jobs?.map((job) => (
                <div key={job.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1A1A1A]">
                  <div>
                    <p className="text-sm text-white font-ibm capitalize">
                      {job.payload?.content_type?.replace('_', ' ') || job.type?.replace('_', ' ')}
                    </p>
                    <p className="text-xs text-[#525252] font-jetbrains mt-0.5">{job.id.slice(0, 8)}</p>
                  </div>
                  <Badge className={statusColor[job.status] || ''} variant="outline">
                    {statusLabel[job.status] || job.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-lg text-white">Son İçerikler</CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.recent_content || data.recent_content.length === 0) && (
              <p className="text-[#737373] text-sm font-ibm py-4">Henüz içerik yok</p>
            )}
            <div className="space-y-3">
              {data.recent_content?.map((item) => (
                <div key={item.id} className="p-3 bg-[#0A0A0A] rounded-md border border-[#1A1A1A]">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-white font-ibm line-clamp-1">{item.title}</p>
                    <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 text-xs capitalize ml-2 shrink-0">
                      {item.type?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-[#737373] line-clamp-2 font-ibm">{item.content?.slice(0, 120)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
